import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Star, Clock, Users, BookOpen, Video, MessageCircle, Award, ChevronRight, Shield, CreditCard } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Normalize track parameter - convert underscores to hyphens and default to apostolic-prophetic
  const getInitialTrack = () => {
    const trackParam = searchParams.get('track') || 'apostolic-prophetic';
    // Normalize underscore variants to hyphen format
    if (trackParam === 'apostolic_prophetic') return 'apostolic-prophetic';
    if (trackParam === 'minstrel_worship') return 'minstrel-worship';
    // Only allow valid track values
    if (trackParam === 'apostolic-prophetic' || trackParam === 'minstrel-worship') return trackParam;
    return 'apostolic-prophetic'; // Default fallback
  };
  
  const [selectedTrack, setSelectedTrack] = useState(getInitialTrack());
  const [selectedPlan, setSelectedPlan] = useState('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Main track definitions (only these are shown in the UI)
  const tracks = {
    'apostolic-prophetic': {
      id: 'apostolic-prophetic',
      name: 'Apostolic-Prophetic Leadership',
      description: 'For those called to apostolic and prophetic ministry',
      icon: '🔥',
      color: 'purple',
      gradient: 'from-purple-600 to-purple-700',
      lightBg: 'bg-purple-50',
      border: 'border-purple-600',
      text: 'text-purple-600'
    },
    'minstrel-worship': {
      id: 'minstrel-worship',
      name: 'Minstrel & Worship Leadership',
      description: 'For worship leaders and minstrels called to lead in the Spirit',
      icon: '🎵',
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      lightBg: 'bg-orange-50',
      border: 'border-orange-500',
      text: 'text-orange-600'
    }
  };

  // Get current track color scheme with fallback to default
  const currentTrack = tracks[selectedTrack] || tracks['apostolic-prophetic'];
  const themeColor = currentTrack.color;

  const paymentPlans = [
    {
      id: 'full',
      name: 'Pay in Full',
      price: 1200,
      originalPrice: 1400,
      savings: 200,
      description: 'One-time payment',
      badge: 'BEST VALUE',
      badgeColor: 'bg-green-500',
      features: [
        'Save £200 instantly',
        'Immediate full access',
        'No recurring payments',
        'Priority support'
      ],
      packageSuffix: '_full'
    },
    {
      id: 'quarterly',
      name: 'Quarterly',
      price: 350,
      perText: 'every 3 months',
      totalPrice: 1400,
      payments: 4,
      description: '4 payments of £350',
      badge: 'POPULAR',
      badgeColor: 'bg-purple-500',
      features: [
        'Spread cost over 12 months',
        'Full access from day one',
        '4 easy payments',
        'Flexible option'
      ],
      packageSuffix: '_quarterly'
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: 120,
      perText: 'per month',
      totalPrice: 1440,
      payments: 12,
      description: '12 payments of £120',
      badge: null,
      features: [
        'Lowest upfront cost',
        'Full access from day one',
        'Cancel anytime*',
        'Budget-friendly'
      ],
      packageSuffix: '_monthly'
    }
  ];

  const benefits = [
    { icon: Users, text: 'Private Community Access' },
    { icon: BookOpen, text: 'Full Ebook Library (70+ books)' },
    { icon: Video, text: 'All Video Courses' },
    { icon: MessageCircle, text: 'Unlimited Q&A Sessions' },
    { icon: Award, text: 'Certificate of Completion' },
    { icon: Clock, text: '12-Month Mentorship Journey' }
  ];

  const handleEnroll = async () => {
    setLoading(true);
    setError('');

    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      // Redirect to signup with return URL
      navigate(`/signup?redirect=/pricing&track=${selectedTrack}&plan=${selectedPlan}`);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const trackPrefix = selectedTrack === 'apostolic-prophetic' ? 'apostolic_prophetic' : 'minstrel_worship';
      const packageId = `${trackPrefix}${paymentPlans.find(p => p.id === selectedPlan).packageSuffix}`;

      const response = await fetch(`${backendUrl}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        setError(data.detail || 'Failed to create checkout session');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedTrackData = tracks[selectedTrack];
  const selectedPlanData = paymentPlans.find(p => p.id === selectedPlan);
  
  // Dynamic color classes based on selected track
  const isOrange = selectedTrack === 'minstrel-worship';
  const headerBg = isOrange ? 'bg-gradient-to-r from-orange-600 to-orange-700' : 'bg-purple-900';
  const headerTextMuted = isOrange ? 'text-orange-200' : 'text-purple-200';
  const stepCircleBg = isOrange ? 'bg-orange-500' : 'bg-purple-600';
  const selectedBorder = isOrange ? 'border-orange-500' : 'border-purple-600';
  const selectedBg = isOrange ? 'bg-orange-50' : 'bg-purple-50';
  const checkBg = isOrange ? 'bg-orange-500' : 'bg-purple-600';
  const primaryBtnBg = isOrange ? 'bg-orange-500 hover:bg-orange-600' : 'bg-purple-600 hover:bg-purple-700';
  const accentText = isOrange ? 'text-orange-600' : 'text-purple-600';
  const pageBg = isOrange ? 'from-orange-50 to-white' : 'from-purple-50 to-white';

  return (
    <div className={`min-h-screen bg-gradient-to-b ${pageBg}`}>
      {/* Header */}
      <div className={`${headerBg} text-white py-16 px-4 transition-all duration-300`}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Join the 12-Month Mentorship Program
          </h1>
          <p className={`text-lg sm:text-xl ${headerTextMuted} max-w-2xl mx-auto`}>
            Transform your calling into a powerful ministry with personal guidance from Pastor Obed Ansu
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Step 1: Choose Track */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <div className={`w-8 h-8 rounded-full ${stepCircleBg} text-white flex items-center justify-center font-bold transition-all duration-300`}>1</div>
            <h2 className="text-xl font-semibold text-gray-900">Choose Your Track</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {Object.values(tracks).map((track) => {
              const isSelected = selectedTrack === track.id;
              const trackBorderColor = track.color === 'orange' ? 'border-orange-500' : 'border-purple-600';
              const trackBgColor = track.color === 'orange' ? 'bg-orange-50' : 'bg-purple-50';
              const trackCheckBg = track.color === 'orange' ? 'bg-orange-500' : 'bg-purple-600';
              const hoverBorder = track.color === 'orange' ? 'hover:border-orange-300' : 'hover:border-purple-300';
              
              return (
                <button
                  key={track.id}
                  onClick={() => setSelectedTrack(track.id)}
                  className={`p-6 rounded-xl border-2 text-left transition-all duration-300 ${
                    isSelected
                      ? `${trackBorderColor} ${trackBgColor} shadow-lg`
                      : `border-gray-200 ${hoverBorder} bg-white`
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">{track.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{track.name}</h3>
                      <p className="text-gray-600 text-sm">{track.description}</p>
                    </div>
                    {isSelected && (
                      <div className={`w-6 h-6 rounded-full ${trackCheckBg} flex items-center justify-center`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Choose Payment Plan */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <div className={`w-8 h-8 rounded-full ${stepCircleBg} text-white flex items-center justify-center font-bold transition-all duration-300`}>2</div>
            <h2 className="text-xl font-semibold text-gray-900">Choose Your Payment Plan</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {paymentPlans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                  selectedPlan === plan.id
                    ? `${selectedBorder} bg-white shadow-xl scale-[1.02]`
                    : `border-gray-200 bg-white hover:${selectedBorder.replace('border-', 'border-').replace('-600', '-300').replace('-500', '-300')} hover:shadow-lg`
                }`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${plan.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {plan.badge}
                  </div>
                )}
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">£{plan.price}</span>
                      {plan.perText && (
                        <span className="text-gray-500 text-sm">{plan.perText}</span>
                      )}
                    </div>
                    {plan.savings && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-400 line-through text-sm">£{plan.originalPrice}</span>
                        <span className="text-green-600 text-sm font-medium">Save £{plan.savings}</span>
                      </div>
                    )}
                    {plan.totalPrice && !plan.savings && (
                      <p className="text-gray-500 text-sm mt-1">Total: £{plan.totalPrice}</p>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-4">{plan.description}</p>

                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {selectedPlan === plan.id && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className={`flex items-center justify-center gap-2 ${accentText} font-medium transition-all duration-300`}>
                        <Check className="w-5 h-5" />
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What's Included */}
        <div className="mb-12 bg-gray-50 rounded-2xl p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">What's Included in Your Mentorship</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                <div className={`w-10 h-10 rounded-full ${isOrange ? 'bg-orange-100' : 'bg-purple-100'} flex items-center justify-center transition-all duration-300`}>
                  <benefit.icon className={`w-5 h-5 ${accentText} transition-all duration-300`} />
                </div>
                <span className="text-gray-700 font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary & CTA */}
        <div className={`bg-white rounded-2xl border-2 ${isOrange ? 'border-orange-200' : 'border-purple-200'} p-8 shadow-lg transition-all duration-300`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Your Selection</h3>
              <div className="flex items-center gap-2 text-gray-600">
                <span className="text-2xl">{selectedTrackData.icon}</span>
                <span className="font-medium">{selectedTrackData.name}</span>
                <ChevronRight className="w-4 h-4" />
                <span>{selectedPlanData.name}</span>
              </div>
              <p className={`text-2xl font-bold ${accentText} mt-2 transition-all duration-300`}>
                £{selectedPlanData.price}
                {selectedPlanData.perText && <span className="text-base font-normal text-gray-500"> {selectedPlanData.perText}</span>}
              </p>
            </div>

            <div className="flex flex-col items-center lg:items-end gap-3">
              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}
              <button
                onClick={handleEnroll}
                disabled={loading}
                className={`w-full lg:w-auto px-8 py-4 ${primaryBtnBg} text-white font-semibold rounded-xl transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Proceed to Payment
                  </>
                )}
              </button>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Shield className="w-4 h-4" />
                Secure payment powered by Stripe
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-4">Trusted by mentees across 20+ nations</p>
          <div className="flex justify-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400 fill-current" />
              <span>4.9/5 Rating</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>500+ Graduates</span>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">Frequently Asked Questions</h3>
          <div className="max-w-3xl mx-auto space-y-4">
            <details className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">What happens after I pay?</summary>
              <p className="mt-3 text-gray-600">You'll receive immediate access to your dashboard, community, ebook library, and all course materials. You'll also receive a welcome email with next steps.</p>
            </details>
            <details className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">Can I switch tracks after enrolling?</summary>
              <p className="mt-3 text-gray-600">Yes! Contact our support team within the first 30 days to switch tracks if you feel another path suits you better.</p>
            </details>
            <details className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">What's the difference between payment plans?</summary>
              <p className="mt-3 text-gray-600">All payment plans give you the same full access. The only difference is how you pay: upfront (save £200), quarterly (4 payments), or monthly (12 payments).</p>
            </details>
            <details className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">How do I access my courses and materials?</summary>
              <p className="mt-3 text-gray-600">After payment, log in to your dashboard where you'll find all your courses, ebooks, community access, and appointment booking features.</p>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
